import { ReactElement } from 'react';
import clsx from 'clsx';
import { APIProvider } from '@vis.gl/react-google-maps';
import DetailedFloor from './DetailedFloor';
import {
  DashboardDataFilter,
  DeviceType,
  Group,
  ParametersType
} from '../constants/telosair-interfaces';
import GroupMap from './MapGroup';

type FloorViewComponentProps = {
  onDashBoard?: boolean;
  groups: Group[];
  selectedGroup: number | null;
  onGroupSelection: (data: number, name: string) => void;
  onBack?: () => void;
  selectedDevice?: number | null;
  shouldHighlight?: boolean;
  selectedParameters: ParametersType[];
  setDataFilter?: (filter: DashboardDataFilter | null) => void;
};

/**
 * Function to generate classname with number of groups.
 * @param groupsLength - Number of groups.
 * @returns - Classname.
 */
const getColClassByNumberOfGroups = (groupsLength: number): string => {
  if (groupsLength === 1) {
    return 'lg:grid-cols-1';
  }
  if (groupsLength === 2 || groupsLength === 4) {
    return 'lg:grid-cols-2';
  }
  return 'lg:grid-cols-3';
};

/**
 * A FloorView Component renders floor view of each group and on clicking on a particular group it will render detail view of that group.
 * @param props - Floor View Component Props.
 * @returns - A React Component.
 */
export default function FloorView(props: FloorViewComponentProps): ReactElement {
  const {
    groups,
    selectedGroup,
    onGroupSelection,
    onDashBoard = false,
    onBack,
    selectedDevice,
    shouldHighlight,
    selectedParameters,
    setDataFilter
  } = props;
  const GROUPS_ARRAY = onDashBoard ? groups.slice(0, 6) : groups;
  const SELECTED_GROUP_WITH_DEVICES =
    !selectedDevice && selectedGroup
      ? groups.filter((group) => group.groupId === selectedGroup)[0]
      : null;

  /**
   * Function to handle onclick floormap.
   * @param group - Clicked group.
   */
  const onClickGroup = (group: Group): void => {
    onGroupSelection(group.groupId, group.groupName);
  };
  return (
    <div
      className={clsx(`h-full rounded shadow-md gap-10`, {
        'p-4': !onDashBoard
      })}
    >
      {/* eslint-disable-next-line no-nested-ternary */}
      {SELECTED_GROUP_WITH_DEVICES ? (
        SELECTED_GROUP_WITH_DEVICES.floorPlan === DeviceType.OUTDOOR ? (
          <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
            <GroupMap
              setDataFilter={setDataFilter}
              selectedGroupName={SELECTED_GROUP_WITH_DEVICES.groupName}
              showExpandedView
              selectedParameters={selectedParameters}
              selectedGroup={SELECTED_GROUP_WITH_DEVICES}
              onDashboard={onDashBoard}
              onBack={onBack}
              selectedDevice={selectedDevice}
              shouldHighlight={shouldHighlight}
            />
          </APIProvider>
        ) : (
          <DetailedFloor
            setDataFilter={setDataFilter}
            selectedGroupName={SELECTED_GROUP_WITH_DEVICES.groupName}
            showExpandedView
            selectedParameters={selectedParameters}
            selectedGroup={SELECTED_GROUP_WITH_DEVICES}
            onDashboard={onDashBoard}
            onBack={onBack}
            selectedDevice={selectedDevice}
            shouldHighlight={shouldHighlight}
          />
        )
      ) : (
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 h-full ${getColClassByNumberOfGroups(
            GROUPS_ARRAY.length
          )} ${onDashBoard ? 'gap-x-6 gap-y-6' : 'gap-y-4 gap-x-10'}`}
        >
          {/*
           * key: room name of devices groupped together for ex. Kitchen, Living ...
           * Value: contains device floor image, floor location and device data.
           */}
          {GROUPS_ARRAY.map((group) => (
            <div
              role="button"
              tabIndex={0}
              key={group.groupId}
              onClick={() => onClickGroup(group)}
              onKeyDown={() => onClickGroup(group)}
              className={`cursor-pointer h-52 ${
                GROUPS_ARRAY.length > 6 ? 'md:h-52' : 'md:h-auto'
              } flex flex-1 ${!onDashBoard && 'pb-4'}`}
            >
              {group.floorPlan === DeviceType.OUTDOOR ? (
                <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
                  <GroupMap
                    setDataFilter={setDataFilter}
                    selectedParameters={selectedParameters}
                    selectedGroupName={group.groupName}
                    selectedGroup={group}
                    onDashboard={onDashBoard}
                    selectedDevice={selectedDevice}
                    shouldHighlight={shouldHighlight}
                  />
                </APIProvider>
              ) : (
                <DetailedFloor
                  setDataFilter={setDataFilter}
                  selectedParameters={selectedParameters}
                  selectedGroupName={group.groupName}
                  selectedGroup={group}
                  onDashboard={onDashBoard}
                  selectedDevice={selectedDevice}
                  shouldHighlight={shouldHighlight}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
